import React, { useEffect, useState } from 'react';
import { getConfiguracion, updateConfiguracion, getAuditLog } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Settings, History, Save, Percent, DollarSign, Clock } from 'lucide-react';

export default function ConfiguracionPage() {
    const [config, setConfig] = useState(null);
    const [auditLog, setAuditLog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [configRes, auditRes] = await Promise.all([
                getConfiguracion(),
                getAuditLog(50)
            ]);
            setConfig(configRes.data);
            setAuditLog(auditRes.data);
        } catch (error) {
            toast.error('Error al cargar configuración');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await updateConfiguracion(config);
            toast.success('Configuración guardada');
        } catch (error) {
            toast.error('Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="configuracion-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold">Configuración</h1>
                <p className="text-muted-foreground mt-1">Parámetros del sistema</p>
            </div>

            <Tabs defaultValue="general" className="space-y-6">
                <TabsList>
                    <TabsTrigger value="general" data-testid="tab-general">
                        <Settings className="w-4 h-4 mr-2" />
                        General
                    </TabsTrigger>
                    <TabsTrigger value="auditoria" data-testid="tab-auditoria">
                        <History className="w-4 h-4 mr-2" />
                        Auditoría
                    </TabsTrigger>
                </TabsList>

                {/* General Tab */}
                <TabsContent value="general" className="space-y-6">
                    <form onSubmit={handleSave}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Mora Config */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                                        <Percent className="w-5 h-5" />
                                        Configuración de Mora
                                    </CardTitle>
                                    <CardDescription>
                                        Define los parámetros de cálculo de mora
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="tasa_mora">Tasa de Mora Diaria (%)</Label>
                                        <Input
                                            id="tasa_mora"
                                            type="number"
                                            value={config?.tasa_mora_diaria || ''}
                                            onChange={(e) => setConfig({ ...config, tasa_mora_diaria: parseFloat(e.target.value) })}
                                            step="0.01"
                                            min="0"
                                            data-testid="config-tasa-mora"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Porcentaje diario aplicado sobre el monto vencido
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="dias_leve">Días Mora Leve</Label>
                                            <Input
                                                id="dias_leve"
                                                type="number"
                                                value={config?.dias_mora_leve || ''}
                                                onChange={(e) => setConfig({ ...config, dias_mora_leve: parseInt(e.target.value) })}
                                                min="1"
                                                data-testid="config-dias-leve"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="dias_media">Días Mora Media</Label>
                                            <Input
                                                id="dias_media"
                                                type="number"
                                                value={config?.dias_mora_media || ''}
                                                onChange={(e) => setConfig({ ...config, dias_mora_media: parseInt(e.target.value) })}
                                                min="1"
                                                data-testid="config-dias-media"
                                            />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Limits Config */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                                        <DollarSign className="w-5 h-5" />
                                        Límites de Préstamo
                                    </CardTitle>
                                    <CardDescription>
                                        Define los montos mínimos y máximos
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="limite_min">Monto Mínimo</Label>
                                        <Input
                                            id="limite_min"
                                            type="number"
                                            value={config?.limite_prestamo_min || ''}
                                            onChange={(e) => setConfig({ ...config, limite_prestamo_min: parseFloat(e.target.value) })}
                                            min="0"
                                            data-testid="config-limite-min"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="limite_max">Monto Máximo</Label>
                                        <Input
                                            id="limite_max"
                                            type="number"
                                            value={config?.limite_prestamo_max || ''}
                                            onChange={(e) => setConfig({ ...config, limite_prestamo_max: parseFloat(e.target.value) })}
                                            min="0"
                                            data-testid="config-limite-max"
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="flex justify-end mt-6">
                            <Button type="submit" disabled={saving} data-testid="save-config-btn">
                                <Save className="w-4 h-4 mr-2" />
                                {saving ? 'Guardando...' : 'Guardar Cambios'}
                            </Button>
                        </div>
                    </form>
                </TabsContent>

                {/* Auditoria Tab */}
                <TabsContent value="auditoria">
                    <Card>
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">Bitácora de Auditoría</CardTitle>
                            <CardDescription>
                                Registro de acciones realizadas en el sistema
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Fecha/Hora</TableHead>
                                            <TableHead>Usuario</TableHead>
                                            <TableHead>Acción</TableHead>
                                            <TableHead className="hidden md:table-cell">Detalles</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {auditLog.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} className="text-center py-8">
                                                    <History className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                                    <p className="text-muted-foreground">No hay registros de auditoría</p>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            auditLog.map((log) => (
                                                <TableRow key={log.id} className="animate-fadeIn">
                                                    <TableCell className="font-mono text-sm">
                                                        {log.timestamp?.replace('T', ' ').slice(0, 19)}
                                                    </TableCell>
                                                    <TableCell>{log.usuario_nombre}</TableCell>
                                                    <TableCell>
                                                        <span className="capitalize">
                                                            {log.accion?.replace(/_/g, ' ')}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="hidden md:table-cell">
                                                        <code className="text-xs bg-muted px-2 py-1 rounded">
                                                            {JSON.stringify(log.detalles || {}).slice(0, 50)}...
                                                        </code>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
